import moment from "moment";
import momentTz from "moment-timezone";
import md5 from "blueimp-md5";
import { integrationsMode } from "@/config";

import { MailTypesMap } from "@/constants/common";
import omit from "lodash/omit";

//assets
import emptyPostcardThumbnail from "@/assets/images/postcard-preview.svg";
import emptyLetterThumbnail from "@/assets/images/letters-preview.svg";

/**
 * Transform string to hashed url
 * @param url {string}
 * @returns {string}
 */
export const getHashedUrl = (url) => {
  let outputUrl = url;

  if (url.charAt(url) !== "/") {
    outputUrl = `/${outputUrl}`;
  }

  return `#${outputUrl}`;
};

/**
 * Reformat date to table format
 * @param date
 * @param extended
 * @returns {string}
 */
export const getFormattedDate = (date, extended = false) => {
  try {
    const d = new Date(date);

    const formatted = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

    if (extended) {
      return `${formatted} at ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
    }

    return formatted;
  } catch {
    return "N/A";
  }
};

/**
 * Convert data (opts) from API format to local frontend
 * @param name
 * @param opts
 * @returns {{uuid: *, name: *, createdBy: *, fromAddress: {uuid, name, createdBy, fromAddress, created, deleted, updated, mailed, tags, mailers}, created: (*|moment.Moment), deleted: *, updated: (*|moment.Moment), mailed: *, tags: string[], mailers: *}}
 */
export function convertDataFromApi(name, opts) {
  switch (name) {
    case "campaign":
      return {
        id: opts.id,
        name: opts.name,
        createdBy: opts.created_by_id,
        fromAddress: convertDataFromApi("address", opts),
        created: moment(opts.created_at),
        deleted: opts.deleted_at ? moment(opts.deleted_at) : null,
        updated: moment(opts.updated_at),
        accepted: opts.accepted_at ? moment(opts.accepted_at) : null,
        mailed: opts.mailed_at ? moment(opts.mailed_at) : null,
        tags: opts.tags,
        mail_statistics: opts.mail_statistics,
      };

    case "campaign-letter":
      return {
        id: opts.id,
        name: opts.name,
        createdBy: opts.created_by_id,
        fromAddress: convertDataFromApi("address", opts),
        created: moment(opts.created_at),
        deleted: opts.deleted_at ? moment(opts.deleted_at) : null,
        updated: moment(opts.updated_at),
        accepted: opts.accepted_at ? moment(opts.accepted_at) : null,
        mailed: opts.mailed_at ? moment(opts.mailed_at) : null,
        tags: opts.tags,
        template_id:
          opts.type === "letter" ? opts.letters_template_id : opts.template_id,
        type: opts.type,
        mail_statistics: opts.mail_statistics,
        use_account_address: opts.use_account_address,
      };

    case "contact":
      return {
        id: opts.id,
        firstName: opts.first_name,
        lastName: opts.last_name,
        company: opts.company,
        tags: opts.tags,
        fromAddress: convertDataFromApi("address", opts),
        custom_data: opts.custom_data,
        mail_piece_created: opts.mail_piece_created
          ? moment(opts.mail_piece_created)
          : null,
      };

    case "address":
      return {
        company: opts.company,
        street: opts.address_street,
        unit: opts.address_unit,
        city: opts.address_city,
        state: opts.address_state,
        zip: opts.address_zip,
        country: opts.address_country,
      };

    case "mailer":
      return {
        templateId: opts.template_id,
        percentage: opts.percentage,
      };

    default:
      return {};
  }
}

/**
 * Converts data from forms format to API data format.
 * @param type
 * @param opts
 * @returns {{name: *, address_unit: *, address_street: *, address_city: *, address_country: *, address_zip: *, address_state: *}}
 */
export function convertDataToApi(type, opts) {
  switch (type) {
    case "address":
      return {
        name: opts.name,
        address: {
          unit: opts.unit,
          street: opts.street,
          city: opts.city,
          country: opts.country,
          state: opts.state,
          zip: opts.zip,
        },
        company: opts.company,
      };

    case "addressOld":
      return {
        // name: opts.name,
        address_line_2: opts.unit,
        address_line_1: opts.street,
        address_city: opts.city,
        address_country: opts.country,
        address_zip: opts.zip,
        address_state: opts.state,
      };

    case "contact":
      return {
        ...convertDataToApi("addressOld", opts),
        source: "manual",
        tags: Array.from(opts.tags),
        first_name: opts.firstName,
        last_name: opts.lastName,
        company: opts.company,
        id: opts.id,
        custom_data: opts.customData,
        email: opts.email,
        phone: opts.phone,
      };

    default:
      return {};
  }
}

/**
 * Converts tags data from API to VSelectList (tags select) format
 * @param tags
 * @returns {*}
 */
export const convertTagsToSelectListFormat = (tags) => {
  return tags.map((tag) => ({
    key: tag.id,
    label: tag.name,
    value: tag.id,
  }));
};

/**
 * Convert templates list from API to VSelect format
 * @param templatesList
 * @returns {*}
 */
export const convertTemplatesToSelectFormat = (templatesList) => {
  return templatesList.map((template) => {
    let thumbnail = "";
    //for letters thumbnails
    if (template.thumbnails_urls) {
      thumbnail = template.thumbnails_urls[0] || emptyLetterThumbnail;
    }
    //for postcards thumbnails
    else {
      thumbnail = template.front_thumbnail_url || emptyPostcardThumbnail;
    }
    return {
      key: template.id,
      label: template.name,
      value: template.id,
      img: thumbnail,
    };
  });
};

/**
 * Convert campaigns form data (create/update) for API format
 * @param data
 * @param accountData
 * @param useAccount
 * @returns {{mailers: {percentage: number, template_id: *}[], name: *, tags: any[], address_city: *, address_country: *, address_street: *, address_unit: *, address_zip: *, address_state: *}|{mailers: {percentage: number, template_id: *}[], name: *, tags: any[], address_city: *, address_country: *, address_state: *, address_unit: *, address_zip: *, address_street: *}|{mailers: {percentage: number, template_id: *}[], name: *, tags: any[]}}
 */
export const convertCampaignFormDataToApiFormat = (
  data,
  accountData,
  useAccount = false
) => {
  let out = {
    template_id: data.template,
    name: data.name,
    tags: Array.from(data.tags),
    use_account_address: useAccount,
  };

  let address_city,
    address_unit,
    address_street,
    address_country,
    address_zip,
    address_state,
    company;

  if (useAccount) {
    ({
      address_city,
      address_country,
      address_state,
      address_unit,
      address_zip,
      address_street,
      company,
    } = accountData);
  } else {
    ({
      city: address_city,
      unit: address_unit,
      street: address_street,
      state: address_state,
      country: address_country,
      zip: address_zip,
      company,
    } = data);
  }

  return {
    ...out,
    address_city,
    address_country,
    address_state,
    address_unit,
    address_zip,
    address_street,
    company,
  };
};

/**
 * Convert API template
 * @param template
 * @param campaign_type - 'letter' or 'postcard'
 * @returns {{id: *, edited: *, created: *, type: *, preview: string, size: *, name: *}}
 */
export const getViewTemplateFormat = (template, campaign_type) => {
  return {
    id: template.id,
    edited: template.updated_at,
    created: template.created_at,
    deleted: template.deleted_at,
    type: template.type,
    preview:
      campaign_type === "postcard"
        ? template.front_thumbnail_url
        : template.thumbnails_urls && template.thumbnails_urls[0],
    name: template.name,
    mail_type: template.mail_type,
  };
};

/**
 * Get template size by type
 * @param type
 * @returns {*}
 */
export const getTemplateSize = (type) => {
  switch (type) {
    case "postcard-4x6":
      return {
        width: "6.25in",
        height: "4.25in",
      };
    case "postcard-6x9":
      return {
        width: "9.25in",
        height: "6.25in",
      };
    case "postcard-6x11":
      return {
        width: "11.25in",
        height: "6.25in",
      };
  }
};

/**
 * Measure length per inch
 */
export const measureInchLength = () => {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.top = "-1000px";
  el.style.width = "1in";
  document.body.appendChild(el);
  const { width } = el.getBoundingClientRect();
  document.body.removeChild(el);
  return width;
};

/**
 * Converts object to 2 dimension array.
 * @param obj
 */
export const fromObjectTo2DimArray = (obj) => {
  const out = [];

  for (let key of Object.keys(obj)) {
    // retrieve key of an object
    out.push([key, obj[key]]);
  }

  return out;
};

/**
 * Converts two dimensional array to flat object
 * @param arr
 */
export const from2DimArrayToObject = (arr) => {
  const out = {};

  for (let item of arr) {
    out[item[0]] = item[1];
  }

  return out;
};

/**
 * Make capital letter for first character in string
 * @param str
 * @returns {string}
 */
export const capitalizeFirstLetter = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Make formatting for mail piece chart data
 * Fill array with empty values for non-existing date from backend
 * so it can be on day-by-day sequence
 * @param data
 * @param date_min
 * @param date_max
 * @returns {Array}
 */
export const formatMailPieceChartData = (data, date_min, date_max) => {
  const res = [];

  for (let itm of data) {
    let obj = {
      "4x6-usps-first-class": 0,
      "6x9-usps-first-class": 0,
      "6x11-usps-first-class": 0,
      "b&w-usps-first-class": 0,
      "color-usps-first-class": 0,
      "6x9-usps-standard": 0,
      "6x11-usps-standard": 0,
      "b&w-usps-standard": 0,
      "color-usps-standard": 0,
      date: moment(itm.date)
        .toDate()
        .getTime(),
    };

    if (itm.details && itm.details.length) {
      itm.details.forEach((detail) => {
        let key;
        const { type } = detail;

        // get cropped key for mailpieces
        if (type.indexOf("postcard") !== -1) {
          key = type.split("postcard-")[1];
        } else {
          key = type.split("letter-")[1];
        }

        obj[key] = detail.count;
      });

      obj = {
        ...obj,
        total: itm.total,
        total_cost: itm.total_cost.toLocaleString("en-us"),
      };
    } else {
      obj = {
        ...obj,
        total: 0,
        total_cost: 0,
      };
    }

    res.push(obj);
  }

  return res;
};

/**
 * Function remove given symbol and replace with space.
 * In addition, capitalize first letter of each word
 * @param item
 * @param symbol
 * @returns {*}
 */
export const replaceAndCapitalize = (item, symbol = "_") => {
  const words = item.split(symbol);

  return words.map((word) => capitalizeFirstLetter(word)).join(" ");
};

/** Unique UUID generator
 *
 * @returns {string}
 */
export const generateUUID = () => {
  let d = new Date().getTime();
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    d += performance.now(); // use high-precision timer if available
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const getMailType = (mail_type, prefix = "") => {
  return mail_type ? `${prefix} ${MailTypesMap[mail_type]}` : "1st Class";
};

/**
 * Convert mail_type to different format
 * E.g. "usps_first_class" -> "usps-first-class"
 */

export const convertMailType = (mail_type, revert = false) => {
  const from = revert ? "-" : "_";
  const to = revert ? "_" : "-";
  return mail_type.replace(new RegExp(from, "g"), to);
};

/**
 * Convert template type to size value
 * E.g. "postcard-4x6" -> "4x6"
 */
export const getPostcardSize = (type) => {
  return type ? `${type.split("postcard-")[1]}` : "";
};

/**
 * Convert template type to size value
 * E.g. "postcard-4x6" + "usps_first_class" -> "4x6 PSC 1st Class"
 */
export const getPostcardFullType = (type, mail_type = "usps_first_class") => {
  return type ? `${getPostcardSize(type)} PSC ${getMailType(mail_type)}` : "";
};

/**
 * Converts str 6x9-usps-first-class
 * to 6x9 PSC 1st Class
 * @param str
 * @returns {*}
 */
export const convertFullTypeKeyToString = (str) => {
  const [type, mail_type] = str.split("-usps-");
  return getPostcardFullType(type, convertMailType(`usps-${mail_type}`, true));
};

/**
 * Convert letter boolean color value to text value
 * E.g. "color: true" -> "Color"
 */
export const getLetterColor = (color) => {
  return color ? "Color" : "B&W";
};

/**
 * Get letter type from Color and mail type to single column
 * */

export const getLetterType = (color, mail_type) => {
  return `${getLetterColor(color)} Letter ${getMailType(mail_type)}`;
};

/**
 * Format moment.js time with a given mask
 * @param dt
 * @param mask
 * @returns {*}
 */
export const formatMomentDT = (dt, mask = "MMM D, YYYY [at] h:mm A z") =>
  dt.format(mask);

/**
 * Removes all whitespace characters from string
 * @param string
 * @returns {*}
 */
export const removeWhiteSpaceFromString = (string) =>
  string.replace(/ +?/g, "");

/**
 * Removes slash symbol (/) from a string
 * @param string
 * @returns {*}
 */
export const removeSlashFromString = (string) => string.replace(/\//g, "");

/**
 * CHeck if input char is digit or not
 * Returns true if digit otherwise false
 * @param e
 * @returns {boolean}
 */
export const isNextCharIsDigit = (e) => {
  const keyCode = e.keyCode ? e.keyCode : e.which;
  return !(keyCode < 47 || keyCode > 58);
};

/**
 * Find the max value for a particular array of objects
 * by given object key
 * @param arr
 * @param key
 * @returns {number}
 */
export const getMaxValueFromArrayByKey = (arr, key) =>
  Math.max(...arr.map((item) => item[key]));

/**
 * Find the max value for a particular array of objects
 * by given object key
 * @param arr
 * @param key
 * @returns {number}
 */
export const getMinValueFromArrayByKey = (arr, key) =>
  Math.min(...arr.map((item) => item[key]));

/**
 * Get gravatar url depends on email
 * @param email
 * @returns {string}
 */
export const getGravatarUrl = (email) =>
  `https://www.gravatar.com/avatar/${md5(email)}?d=identicon`;

/**
 * Returns timezones names for select with UTC offset
 * @returns {Array}
 */
export const getTimezonesNamesForSelect = () => {
  const arr = [];
  const names = momentTz.tz.names();
  for (const name of names) {
    if ((name.indexOf("/") < 0 && name !== "UTC") || name.startsWith("Etc/")) {
      continue;
    }
    const data = [];
    data.key = name;
    data.value = name;
    data.label = momentTz.tz(name).format("Z");
    arr.push(data);
  }
  arr.sort((a, b) => {
    if (a.label === b.label) {
      if (a.key === "UTC") {
        return -1;
      }
      return a.key === b.key ? 0 : a.key > b.key ? 1 : -1;
    }
    const afc = a.label.charAt(0);
    const bfc = b.label.charAt(0);
    if (afc === "-") {
      if (bfc === "+") {
        return -1;
      }
      return a.label > b.label ? -1 : 1;
    }
    if (bfc === "-") {
      return 1;
    }
    return a.label > b.label ? 1 : -1;
  });
  arr.forEach((a) => (a.label = `(GMT${a.label}) ${a.key}`));

  return arr;
};

/**
 * Move array item from specific index to an another one
 * @param arr
 * @param fromIndex
 * @param toIndex
 */
export const moveArrayItem = (arr, fromIndex, toIndex) => {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
  return arr;
};

/**
 * Retrieve string representation for mailpiece source
 * @param source
 * @returns {string}
 */
export const processMailpieceSource = (source) => {
  let str;

  switch (source) {
    case "mail-piece-resend":
    case "mail_piece_resend":
      str = "Resend";
      break;

    case "csv":
      str = source.toUpperCase();
      break;

    case "hubspot":
      str = "HubSpot";
      break;

    default:
      str = capitalizeFirstLetter(source);
  }

  return str;
};

/**
 * Convert letter format from VForm to API
 * @param data
 * @returns {{color: boolean, html: *, double_sided: boolean, address_placement: string, perforated: boolean, return_envelope: boolean, additional_service}}
 */
export const convertLetterToApiFormat = (data) => ({
  color: data.color === "color",
  html: data.html,
  file_url: data.file_url,
  double_sided: data.page_options === "double_sided",
  address_placement: data.blank_page ? "insert_blank_page" : "top_first_page",
  perforated: data.perforated ? data.perforated.last : null,
  return_envelope: !!data.return_envelope,
  mail_type: data.mail_type,
  additional_service: data.additional_service || null,
  product_code_id: data.product_code_id || null,
});

/**
 * Get last saved page for onboarding sequence
 * (from local storage)
 */
export const getLastCompletedPageFromLS = () => {
  const lsLastCompleted = localStorage.getItem("lastCompletedPage");
  return lsLastCompleted && +lsLastCompleted;
};

/**
 * Collect all of the address fields, which actually started with prefix 'address-'
 * and return object with key-value paris without the prefix
 * @param data
 */
export const collectAddressObjectFromData = (data) => {
  const dataKeys = Object.keys(data);
  const addrKeys = dataKeys.filter((item) => item.includes("address_"));
  const returnObj = {};

  addrKeys.forEach((key) => {
    returnObj[key.split("address_")[0]] = data[key];
  });

  return returnObj;
};

/**
 * Convert billing information from form to api format
 */
export const convertBillingInfoToApiFormat = (opts) => {
  let {
    billing_email,
    billing_phone,
    billing_contact_full_name,
    ...billing_address
  } = opts;

  if (!billing_address.address_line_2) {
    billing_address = omit(billing_address, "address_line_2");
  }

  return {
    billing_info: {
      billing_contact_full_name,
      billing_email,
      billing_phone,
      billing_address,
    },
  };
};

/**
 *
 * Convert base api's error response to specific message for user
 *
 * */
export const apiErrorHandler = (e, newErrorFormat) => {
  const errorData = e.response && e.response.data;

  // For debug
  // const errorData = [
  //   {
  //     code_name: "bla",
  //     detail: "Some Details",
  //     id: 1,
  //     meta: { field: "street", original_value: "AD" },
  //     title: "this is title"
  //   },
  //   {
  //     code_name: "bla2",
  //     id: 2,
  //     meta: { field: "unit", original_value: "Paris" },
  //     title: "this is title 2"
  //   }
  // ];

  if (newErrorFormat && Array.isArray(errorData)) {
    const errorsArr = errorData.map((error) => {
      return {
        meta: error.meta,
        message: error.detail || error.title,
      };
    });
    return { status: e.response.status, errors: errorsArr };
  } else if (e.response) {
    return {
      status: e.response.status,
      message:
        errorData && errorData.errors ? errorData.errors.join(". ") : errorData,
    };
  }
  return e.message;
};

/**
 * Get current using backend main URL
 * @returns {string}
 */
export const getBackendURL = () => {
  let prefix;
  let domain = "io";
  switch (integrationsMode) {
    case "development":
      prefix = "next.";
      break;
    case "staging":
      prefix = "staging.";
      domain = "com";
      break;
    default:
      prefix = "";
      break;
  }
  return `https://internal.${prefix}inkit.${domain}`;
};

/**
 * Get integration URL
 * @param templateId
 * @param apiToken
 * @param integrationName
 * @returns {string}
 */
export const getIntegrationURL = (templateId, integrationName, apiToken) => {
  const knownIntegrationsURLs = ["drip", "hubspot"];

  if (!knownIntegrationsURLs.includes(integrationName)) {
    throw Error(
      `Integration ${integrationName} is not known. Known list: ${knownIntegrationsURLs.join(
        ", "
      )}.`
    );
  }

  let prefix = `${getBackendURL()}/integrations/`;
  let url = "";

  switch (integrationName) {
    case "drip":
      url = `${prefix}v1/${templateId}/webhook`;
      break;

    case "hubspot":
      url = `${prefix}hubspot/${templateId}/webhook/${apiToken}`;
  }

  return url;
};

/**
 * Push or delete element of array
 * push if element is unique
 * delete if element is already in array
 * @param elem
 * @param arr
 * @param withPush
 * @param withPull
 */

export const insertOrDeleteByKey = (
  elem,
  arr,
  withPush = true,
  withDelete = true
) => {
  let newArr = arr;
  const elIndex = newArr.indexOf(elem);
  if (~elIndex) {
    withDelete ? newArr.splice(elIndex, 1) : newArr;
  } else {
    withPush ? newArr.push(elem) : newArr;
  }
  return newArr;
};

export const isStringContainsEmoji = (str) => {
  return /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gi.test(
    str
  );
};
